<template>
  <Bar :data="chartData" :options="chartOptions" :plugins="chartPlugins"></Bar>
</template>

<style lang="scss" scoped>
</style>

<script setup lang="ts">
import { reactive, onBeforeMount, onMounted, inject } from "vue";
import { useDark } from "@vueuse/core";
import moment from "moment";

import { Bar } from "vue-chartjs";
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, elements } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

import { useParticipations } from "@/queries/participant.query";
import { ParticipantContext } from "@/types/participant";


const { locale } = useI18n();

const isDark = useDark();
const route = useRoute();

const { data: participations, isPending: isParticipationsPending } = useParticipations();

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, ChartDataLabels)

function getLastSixMonths() {
  moment.locale(locale.value); // set the desired locale to user locale
  const months = [];

  for (let i = 0; i < 6; i++) {
    let monthName = moment().subtract(i, 'months').format('MMMM');
    months.unshift(
      monthName.charAt(0).toUpperCase() + monthName.slice(1) // capitalize month first letter
    );
  }

  return months;
}

function getLastSixMonthsNumber() {
  moment.locale(locale.value); // set the desired locale to user locale
  const months = [];
  for (let i = 0; i < 6; i++) {
    let monthNum = moment().subtract(i, 'months').toDate();
    months.unshift(monthNum);
  }
  return months;
}

const chartData = computed(() => {
  const lastSixMonths = getLastSixMonthsNumber();
  let data = [0, 0, 0, 0, 0, 0];

  if (isParticipationsPending.value) return { labels: [], datasets: [] };
  participations.value.filter((p) => p.state === 2 && p?.results).forEach((participant) => {
    const evalDate = new Date(participant?.results.completed_at);
    const evalMonth = evalDate.getMonth();
    const index = lastSixMonths.findIndex((d) => d.getMonth() === evalMonth);
    data[index] += 1;
  });

  return {
    labels: getLastSixMonths(),
    datasets: [
      {
        data: data,
        barThickness: 12,
        categoryPercentage: 1,
        backgroundColor: "rgba(242, 203, 54, 1)",
      },
    ],
  };
});

const chartOptions = computed(() => {
  return {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        left: '0',
        right: '0',
        top: '30',
        bottom: '0'
      },
    },
    elements: {
      bar: {
        borderRadius: 8,
      }
    },
    scales: {
      x: {
        ticks: {
          display: true,
          font: {
            family: 'Inter',
            size: '12',
          },
          color: isDark.value ? '#FFFFFF' : '#1D1D1D',
        },
        grid: {
          display: false,
        },
      },
      y: {
        display: false,
      },
    },
    plugins: {
      legend: { display: false }, // remove legend if not needed
      tooltip: {
        enabled: false // ❌ disables tooltips
      },
      datalabels: {
        anchor: 'end',
        align: 'end',
        font: {
          family: 'Inter',
          size: '12',
        },
        color: isDark.value ? '#FFFFFF' : '#1D1D1D',
      }

    },
  }
});

const chartPlugins = computed(() => {
  return [ChartDataLabels];
});
</script>